import React, { useState, useRef, useEffect } from "react";
import { useParams } from "react-router-dom";
import { Download, Play, Pause, SkipBack, SkipForward, Building2 } from "lucide-react";
import digikoreLogo from "../../assets/digikorestudios.png";
import { downloadSong, getfinalUploadedAudiobyId } from "../../redux/reduxSlices/audioUploadSlice";
import { useDispatch, useSelector } from "react-redux";
import { ImSpinner2 } from 'react-icons/im';

function SongDeliveryPage() {
  const { id } = useParams();
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [currentSongIndex, setCurrentSongIndex] = useState(0);
  const audioRef = useRef(null);
  const dispatch = useDispatch()
  const { getFinalAudioById, isLoading } = useSelector((state) => state.audioUpload);
  const [songs, setSongs] = useState([]);

  useEffect(() => {
    if (id) {
      dispatch(getfinalUploadedAudiobyId(id));
    }
  }, [id, dispatch]);

  useEffect(() => {
    if (getFinalAudioById !== null && getFinalAudioById.songs) {
      setSongs(getFinalAudioById.songs);
    }
  }, [getFinalAudioById]);

  // Effect to handle audio metadata and time updates
  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;

    const setAudioData = () => {
      setDuration(audio.duration);
    };

    const updateTime = () => {
      setCurrentTime(audio.currentTime);
    };

    audio.addEventListener("loadedmetadata", setAudioData);
    audio.addEventListener("timeupdate", updateTime);

    return () => {
      audio.removeEventListener("loadedmetadata", setAudioData);
      audio.removeEventListener("timeupdate", updateTime);
    };
  }, [currentSongIndex, songs]); // Re-run when song changes

  const togglePlayPause = () => {
    if (!audioRef.current || songs.length === 0) return;
    if (isPlaying) {
      audioRef.current.pause();
    } else {
      audioRef.current.play();
    }
    setIsPlaying(!isPlaying);
  };

  const playSong = (index) => {
    if (index === currentSongIndex && isPlaying) {
      audioRef.current.pause();
      setIsPlaying(false);
    } else {
      setCurrentSongIndex(index);
      setIsPlaying(true);
      // Use setTimeout to ensure state update before playing
      setTimeout(() => {
        if (audioRef.current) {
          audioRef.current.play();
        }
      }, 0);
    }
  };

  const handleNext = () => {
    if (songs.length === 0) return;
    const nextIndex = (currentSongIndex + 1) % songs.length;
    setCurrentSongIndex(nextIndex);
    setIsPlaying(true);
    setTimeout(() => {
      if (audioRef.current) {
        audioRef.current.play();
      }
    }, 0);
  };

  const handlePrevious = () => {
    if (songs.length === 0) return;
    const prevIndex = (currentSongIndex - 1 + songs.length) % songs.length;
    setCurrentSongIndex(prevIndex);
    setIsPlaying(true);
    setTimeout(() => {
      if (audioRef.current) {
        audioRef.current.play();
      }
    }, 0);
  };

  const handleProgressChange = (e) => {
    const newTime = (e.target.value / 100) * duration;
    if (audioRef.current) {
      audioRef.current.currentTime = newTime;
      setCurrentTime(newTime);
    }
  };

  useEffect(() => {
    const isMobile = () =>
      /Android|iPhone|iPad|iPod|Opera Mini|IEMobile/i.test(navigator.userAgent) ||
      window.innerWidth < 768;

    if (isMobile()) return;

    const handleKeyDown = (e) => {
      if (
        e.keyCode === 123 ||
        (e.ctrlKey && e.shiftKey && (e.key === "I" || e.key === "J")) ||
        (e.ctrlKey && e.key === "u")
      ) {
        e.preventDefault();
        alert("Inspect is disabled on this page.");
      }
    };

    const handleContextMenu = (e) => e.preventDefault();

    document.addEventListener("keydown", handleKeyDown);
    document.addEventListener("contextmenu", handleContextMenu);

    const threshold = 160;
    const checkDevTools = setInterval(() => {
      const widthGap = window.outerWidth - window.innerWidth;
      const heightGap = window.outerHeight - window.innerHeight;

      if (widthGap > threshold || heightGap > threshold) {
        alert("Developer tools detected! Please close them to continue.");
        window.location.reload();
      }
    }, 1000);

    return () => {
      document.removeEventListener("keydown", handleKeyDown);
      document.removeEventListener("contextmenu", handleContextMenu);
      clearInterval(checkDevTools);
    };
  }, []);

  const handleDownload = async (songUrl, filename = "song.mp3") => {
    const payload = {
      song_url: songUrl,
      filename: filename
    }

    try {
      const response = await dispatch(downloadSong(payload));

      // For axios with responseType: 'blob'
      const blob = new Blob([response.payload.data], { type: 'audio/mpeg' });

      // For fetch approach
      // const blob = response.payload;

      const downloadUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(downloadUrl);

    } catch (error) {
      console.error('Download error:', error);
    }
  };

  const formatTime = (time) => {
    if (isNaN(time)) return "0:00";
    const minutes = Math.floor(time / 60);
    const seconds = Math.floor(time % 60);
    return `${minutes}:${seconds < 10 ? "0" : ""}${seconds}`;
  };

  const getCurrentSong = () => {
    return songs[currentSongIndex] || null;
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-100 to-gray-200 flex items-center justify-center">
        <ImSpinner2 className="animate-spin text-orange-500 text-4xl" />
      </div>
    );
  }

  if (!songs || songs.length === 0) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-100 to-gray-200 flex items-center justify-center">
        <div className="text-center">
          <ImSpinner2 className="animate-spin text-orange-500 text-4xl mx-auto mb-4" />
          <p className="text-gray-600">Loading audio...</p>
        </div>
      </div>
    );
  }

  const currentSong = getCurrentSong();

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-100 to-gray-200 p-4">
      <div className="flex flex-col items-center justify-center mb-8 mt-20">
        <img
          src={digikoreLogo}
          alt="Digikore Logo"
          className="h-12 mb-6 w-48"
        />
        <div className="text-center">
          <h1 className="text-2xl font-bold bg-gradient-to-r from-purple-600 to-blue-600 bg-clip-text text-transparent mb-2">
            Your Jingles
          </h1>
          <p className="text-gray-600 text-sm">
            Listen and ♡ your favorite tracks
          </p>
        </div>
        {getFinalAudioById?.company_name && (
          <div className="mt-4 bg-white/80 backdrop-blur-sm rounded-xl px-6 py-3 shadow-lg border border-white/20">
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 bg-gradient-to-r from-purple-500 to-blue-500 rounded-lg flex items-center justify-center">
                <Building2 className="w-4 h-4 text-white" />
              </div>
              <div className="text-center">
                {/* <p className="text-sm text-gray-600">Company</p> */}
                <p className="font-semibold text-gray-800">
                  {getFinalAudioById.company_name}
                </p>
              </div>
              {/* {getFinalAudioById?.order_id && (
                <div className="border-l border-gray-300 pl-3">
                  <p className="text-sm text-gray-600">Order</p>
                  <p className="font-semibold text-gray-800">
                    #{getFinalAudioById.order_id}
                  </p>
                </div>
              )} */}
            </div>
          </div>
        )}
      </div>
      <div className="flex flex-col lg:flex-row justify-center items-start gap-8 max-w-4xl mx-auto px-4">
        {/* Playlist */}
        <div className="bg-white rounded-2xl shadow-xl p-6 w-full lg:max-w-md">
          <h3 className="text-lg font-bold text-gray-800 mb-4">Playlist ({songs.length} tracks)</h3>
          <div className="space-y-3 max-h-96 overflow-y-auto pr-2">
            {songs.map((song, index) => (
              <div
                key={song.song_id}
                className={`flex items-center justify-between p-3 rounded-lg border transition-all cursor-pointer ${index === currentSongIndex
                  ? 'bg-blue-50 border-blue-200'
                  : 'bg-gray-50 border-gray-200 hover:bg-gray-100'
                  }`}
                onClick={() => playSong(index)}
              >
                <div className="flex items-center space-x-3 flex-1 min-w-0">
                  <div
                    className={`w-8 h-8 rounded-full flex items-center justify-center ${index === currentSongIndex && isPlaying
                      ? 'bg-blue-500 text-white'
                      : index === currentSongIndex
                        ? 'bg-blue-100 text-blue-500'
                        : 'bg-gray-200 text-gray-500'
                      }`}
                  >
                    {index === currentSongIndex && isPlaying ? (
                      <Pause className="w-3 h-3" />
                    ) : (
                      <Play className="w-3 h-3 ml-0.5" />
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-gray-800 truncate">
                      {song.filename}
                    </p>
                    <p className="text-xs text-gray-500">
                      Track {index + 1}
                    </p>
                    <p className="text-xs text-gray-500">{song.uploaded_at ? new Date(song.uploaded_at).toLocaleDateString(
                      "en-IN",
                      {
                        day: "2-digit",
                        month: "short",
                        year: "numeric",
                      }
                    )
                      : "- /-/--"} </p>
                  </div>
                </div>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    handleDownload(song.url, song.filename);
                  }}
                  className="text-gray-400 hover:text-gray-600 p-1 rounded transition-colors"
                >
                  <Download className="w-4 h-4" />
                </button>
              </div>
            ))}
          </div>
        </div>

        {/* Main Player */}
        <div className="bg-white rounded-2xl shadow-xl p-8 w-full lg:max-w-md">
          {/* Track Info */}
          <div className="text-center mb-6">
            <h2 className="text-xl font-bold text-gray-800 truncate">
              {getFinalAudioById?.order_id ? `Order #${getFinalAudioById.order_id}` : 'Your Audio'}
            </h2>
            <p className="text-gray-500 text-sm mt-1">
              Track {currentSongIndex + 1} of {songs.length}
            </p>
            <p className="text-gray-600 text-sm mt-2 truncate">
              {currentSong?.filename || 'Current Track'}
            </p>
          </div>

          {/* Progress Bar */}
          <div className="mb-4">
            <input
              type="range"
              min="0"
              max="100"
              value={duration ? (currentTime / duration) * 100 : 0}
              onChange={handleProgressChange}
              className="w-full h-1.5 bg-gray-200 rounded-full appearance-none cursor-pointer 
          [&::-webkit-slider-thumb]:appearance-none 
          [&::-webkit-slider-thumb]:h-4 
          [&::-webkit-slider-thumb]:w-4 
          [&::-webkit-slider-thumb]:rounded-full 
          [&::-webkit-slider-thumb]:bg-gray-700"
            />
          </div>

          {/* Time Display */}
          <div className="flex justify-between text-xs text-gray-500 mb-6">
            <span>{formatTime(currentTime)}</span>
            <span>{formatTime(duration)}</span>
          </div>

          {/* Controls */}
          <div className="flex justify-center items-center gap-4">
            {/* Previous Button */}
            <button
              onClick={handlePrevious}
              className="text-gray-600 hover:text-gray-800 transition-colors"
              disabled={songs.length <= 1}
            >
              <SkipBack className="w-5 h-5" />
            </button>

            {/* Play / Pause */}
            <button
              onClick={togglePlayPause}
              className="bg-gray-800 hover:bg-gray-900 text-white rounded-full w-14 h-14 flex items-center justify-center transition-all duration-200 shadow-lg hover:shadow-xl active:scale-95"
              disabled={!currentSong?.url}
            >
              {isPlaying ? (
                <Pause className="w-6 h-6" />
              ) : (
                <Play className="w-6 h-6 ml-0.5" />
              )}
            </button>

            {/* Next Button */}
            <button
              onClick={handleNext}
              className="text-gray-600 hover:text-gray-800 transition-colors"
              disabled={songs.length <= 1}
            >
              <SkipForward className="w-5 h-5" />
            </button>

            {/* Download Button */}
            {/* <button
              onClick={() => handleDownload(currentSong?.url, currentSong?.song_id)}
              className="bg-gray-200 hover:bg-gray-300 text-gray-800 rounded-full w-12 h-12 flex items-center justify-center transition-all duration-200 shadow-md hover:shadow-lg active:scale-95 ml-2"
              disabled={!currentSong?.url}
            >
              <Download className="w-5 h-5" />
            </button> */}
          </div>

          {/* Hidden Audio Element */}
          <audio
            ref={audioRef}
            src={currentSong?.url}
            preload="metadata"
            onEnded={() => {
              setIsPlaying(false);
              // Auto-play next song when current ends
              if (songs.length > 1) {
                handleNext();
              }
            }}
          />
        </div>
      </div>
    </div>
  );
}

export default SongDeliveryPage;